/**
 * Text direction of content that carries no direction of its own.
 *
 * Answer options in Checkpoint, Poll, Exam and Form are plain strings with nowhere to store a
 * direction, so the server reads it off the text and renders it on the row that holds the option.
 * The workspace translator replaces that text in place long after the page was rendered, so a row
 * marked with data-auto-dir reads its direction again whenever one of its translations lands.
 * Rows without the marker keep whatever direction they were given, which is what rich text needs:
 * there the author chose the direction with the editor buttons.
 *
 * A row decides by its data-dir-source elements, in document order, which are the same candidates
 * in the same order that LocaleHelper::detectTextDirection was given on the server. Anything else
 * inside the row is ignored, so a field description or a feedback text cannot pull the row away
 * from the direction its title asked for.
 *
 * A refresh rewrites the dir attribute and nothing else, so anything directional in the row has to
 * follow that attribute rather than be picked while rendering. Mirror arrows and other directional
 * assets in CSS under [dir="rtl"]. Choosing between a left and a right variant in PHP leaves them
 * pointing the way the untranslated text read.
 */
howspace.components.textDirection = (() => {
    'use strict';

    let probes = null;
    const cache = new Map();

    // A strong left to right letter and a strong right to left one, appended to the text being
    // measured. They only get to decide when the text carries no strong character of its own.
    const leftToRightLetter = 'A';
    const rightToLeftLetter = '\u05D0';

    /**
     * Two spans that resolve dir="auto". Asking the browser keeps this on exactly the rule the
     * server matches with the Unicode bidi character types, instead of a list of right to left
     * scripts here that would drift away from it.
     */
    const getProbes = () => {
        if (!probes) {
            const host = document.createElement('div');
            host.setAttribute('aria-hidden', 'true');
            host.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;visibility:hidden;';
            host.innerHTML = '<span dir="auto"></span><span dir="auto"></span>';
            document.body.appendChild(host);
            probes = host.querySelectorAll('span');
        }
        return probes;
    };

    /**
     * Direction of a single piece of text, decided by its first strong directional character.
     *
     * Each probe gets the text followed by a strong letter, one of each direction. When the text
     * has a strong character of its own it decides both probes and they agree. When it has none
     * the appended letters decide, the probes disagree, and the caller moves on to the next
     * candidate the way the server walks its list. Measuring the bare text cannot tell those two
     * apart, because dir="auto" reports left to right for text with no strong character at all.
     */
    const detect = text => {
        if (!text) {
            return null;
        }
        if (cache.has(text)) {
            return cache.get(text);
        }

        const [towardsLeftToRight, towardsRightToLeft] = getProbes();
        towardsLeftToRight.textContent = text + leftToRightLetter;
        towardsRightToLeft.textContent = text + rightToLeftLetter;

        const asLeftToRight = getComputedStyle(towardsLeftToRight).direction;
        const asRightToLeft = getComputedStyle(towardsRightToLeft).direction;
        const direction = asLeftToRight === asRightToLeft ? asLeftToRight : null;

        cache.set(text, direction);
        return direction;
    };

    /**
     * Read the direction of a container off its own direction sources, in the order the server
     * read them. Markers such as the A, B, C of an Exam option are not sources, so they never
     * decide the direction of the row they sit in.
     */
    const refresh = container => {
        const elem = $(container);
        if (!elem.length) {
            return;
        }

        const sources = elem.find('[data-dir-source]').addBack('[data-dir-source]').toArray();
        if (!sources.length) {
            // Nothing to read the direction from, so leave the row with what it was given.
            return;
        }

        for (const source of sources) {
            const direction = detect(source.textContent.trim());
            if (direction) {
                elem.attr('dir', direction);
                return;
            }
        }

        // No candidate held a strong character, which is where the server ends up returning ltr.
        // Without this a row translated into digits or punctuation would keep the direction of
        // the language it was written in.
        elem.attr('dir', 'ltr');
    };

    real.onTierReady(real.TIER_2, () => {
        $(document).on(
            howspace.components.translator.TRANSLATION_COMPLETED,
            '[data-auto-dir] .translatable',
            function () {
                refresh($(this).closest('[data-auto-dir]'));
            }
        );
    });

    return {
        detect: detect,
        refresh: refresh,
    };
})();
